// @flow
import { getAuthToken, getBidderId } from './user';
import { postLAPayment, postStripePayment } from '../api/payment';
import { transformAddressForStripe, translateAddressAccents } from '../../utils/address';
import { type Address } from '@liveauctioneers/caterwaul-components/types/Address';
import { createSelector } from 'reselect';
import { DISMISS_MODAL } from './modal';
import { fetchWonItemsIfNeeded } from './wonItems';
import { getDeployment } from './config';
import { getSessionId } from '../modules/user';
import { getStripeErrorMessageFromResponse } from '../../utils/getStripeErrorMessageFromResponse';
import type { GlobalState } from '../rootReducer';
import paymentProviders from '../../enums/paymentProviders';
import { type StripeBillingAddress } from '../../types/StripeBillingAddress';

/* Action Types */
export const SUBMIT_PAYMENT_FAIL = 'la/ui/payment/FAIL';
export const SUBMIT_PAYMENT_REQUEST = 'la/ui/payment/REQUEST';
export const SUBMIT_PAYMENT_SUCCESS = 'la/ui/payment/SUCCESS';

// reducer
const DEFAULT_STATE = {
    error: false,
    errorMessage: null,
    submitted: false,
    success: false,
};

export type State = {|
    ...typeof DEFAULT_STATE,
    errorMessage: ?string,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case SUBMIT_PAYMENT_REQUEST:
            return {
                ...state,
                error: false,
                errorMessage: null,
                submitted: true,
                success: false,
            };
        case SUBMIT_PAYMENT_SUCCESS:
            return {
                ...state,
                submitted: false,
                success: true,
            };
        case SUBMIT_PAYMENT_FAIL:
            return {
                ...state,
                error: true,
                errorMessage: getStripeErrorMessageFromResponse(action.payload),
                submitted: false,
                success: false,
            };
        case DISMISS_MODAL:
            return DEFAULT_STATE;
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.payment;

export const getUiSubmitPayment = createSelector(stateSelector, state => state);

/* ACTION CREATORS */
type SubmitPaymentParams = {
    address?: Address,
    amount?: number,
    cardToken?: string,
    catalogId?: number,
    currency?: string,
    invoiceId?: string,
    providerId: number,
};
export const submitPayment = ({
    address = {},
    amount = 0,
    cardToken = '',
    catalogId = 0,
    currency = '',
    invoiceId = '',
    providerId = paymentProviders.STRIPE,
}: SubmitPaymentParams) => async (dispatch: Function) => {
    // translate accents because mainhost
    const translatedAddress = translateAddressAccents(address);

    if (providerId === paymentProviders.STRIPE) {
        const stripeAddress = transformAddressForStripe(translatedAddress);
        await dispatch(submitStripePayment({ address: stripeAddress, amount, cardToken, catalogId, currency }));
    } else {
        await dispatch(submitLAPayment({ address: translatedAddress, invoiceId }));
    }
};

type submitStripePaymentParams = {
    address: StripeBillingAddress,
    amount: number,
    cardToken: string,
    catalogId: number,
    currency: string,
};
export const submitStripePayment = ({
    address,
    amount,
    cardToken,
    catalogId,
    currency,
}: submitStripePaymentParams) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);
        const sessionId = getSessionId();

        dispatch({
            payload: bidderId,
            type: SUBMIT_PAYMENT_REQUEST,
        });
        const response = await postStripePayment({
            address,
            amount,
            authToken,
            bidderId,
            cardToken,
            catalogId,
            currency,
            deployment,
            sessionId,
        });

        dispatch({
            meta: { actionTime: Date.now() },
            payload: response.data,
            type: SUBMIT_PAYMENT_SUCCESS,
        });
        return dispatch(fetchWonItemsIfNeeded({ force: true, page: 1, pageSize: 1250 }));
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: SUBMIT_PAYMENT_FAIL,
        });
        throw error;
    }
};

type submitLAPaymentParams = { address: Address, invoiceId: string };
export const submitLAPayment = ({ address, invoiceId }: submitLAPaymentParams) => async (
    dispatch: Function,
    getState: Function
) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);

        dispatch({
            payload: bidderId,
            type: SUBMIT_PAYMENT_REQUEST,
        });
        const response = await postLAPayment({
            address,
            authToken,
            bidderId,
            deployment,
            invoiceId,
        });

        dispatch({
            meta: { actionTime: Date.now() },
            payload: response.data,
            type: SUBMIT_PAYMENT_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: SUBMIT_PAYMENT_FAIL,
        });
        throw error;
    }
};
